import { z } from "zod";
//#region src/machine-type-catalog.ts
/**
* The two GKE NodePool boot-disk types the platform provisions (hyperdisk boot-disk D2). This
* `as const` tuple is the SINGLE list — every zod enum that accepts a boot-disk type is built
* from it (`provisionedNodeTypeSchema.diskType`, `machineTypeCatalogEntrySchema.bootDiskType`,
* and in `@zion/sdk-cp-dp-contracts` the `diskType` of `app-pool-node-group.ts` and of the
* deprecated `dedicatedNodePoolSpecSchema`), and the XRD's
* `enum: [pd-ssd, hyperdisk-balanced]` mirrors it. A second hand-written copy anywhere is drift
* with a promise attached — build the enum, do not retype the strings.
*
* ⛔ `pd-balanced` is DELIBERATELY ABSENT. Adding a third value widens the XRD enum, which is the
* SOLE YAML-injection guard on the go-template's `diskType` interpolation sink (D2 / D-Z6) — it
* is a security-relevant change, not a convenience one. See the H3 note in
* {@link HYPERDISK_ONLY_MACHINE_FAMILIES}.
*/
const BOOT_DISK_TYPES = ["pd-ssd", "hyperdisk-balanced"];
/**
* GCE machine-type FAMILIES that cannot boot from `pd-ssd` and must use Hyperdisk
* (hyperdisk boot-disk D4, membership widened to fourteen by D11). Lowercase family prefixes —
* the substring of a machine type up to its first `-`.
*
* WHY THIS LIST EXISTS: on 2026-08-08 every node creation for `c4d-standard-32-lssd` in DP `app`
* failed in all three zones with `UNSUPPORTED_OPERATION: pd-ssd disk type cannot be used by
* c4d-standard-32-lssd machine type`, because all three composition node-pool fragments hardcoded
* `pd-ssd`. Demand pools are created at `initialNodeCount: 0, min: 0`, so GCE never validates the
* machineType×diskType pairing at pool-create — the pool goes RUNNING, Crossplane goes Ready, and
* the invalid config only surfaces at first scale-up. Identical wedge class to #771 (zones), one
* field over.
*
* ⛔ NEVER move a pd-ssd-CAPABLE family into this list (e2, n1, n2, n2d, c2, c2d, c3, c3d, g2, g4,
* a2, a3, m1, m2, m3, t2d, t2a, z3). Changing `nodeConfig.diskType` on an existing GKE node pool
* is an IN-PLACE update that RECREATES the pool's nodes (plan-set D19, measured on
* provider-gcp-container v1.8.3 — it is NOT a ForceNew/pool replacement, as this comment
* originally claimed). So a family added here triggers a rolling node recreation across every
* existing pool of that family, fleet-wide. The cost of a wrong addition is that churn — on pools
* with running nodes, real disruption; the cost of a wrong omission is one wedged pool. They are
* not symmetric — but neither is free.
*
* ⛔ AN UNKNOWN FAMILY DEFAULTS TO `pd-ssd`, WHICH IS THE WEDGE. Every GCE family shipped since C4
* has been Hyperdisk-only, so this list goes stale in the dangerous direction by default. The
* maintenance step is: for each family in the discovered region catalog, read THAT FAMILY'S OWN
* machine-family page and check whether `pd-ssd` appears in its enumerated block-storage list.
* ⛔ Do NOT verify against the machine-series × Persistent-Disk-type compatibility matrix at
* .../compute/docs/disks/persistent-disks — fetched twice on 2026-08-08 it returned CONTRADICTORY
* values for the same rows (`—` then `✓` for C4/C4D/N4), and the second reading is provably wrong
* against C4D's and N4's own pages. It is not a usable source in either direction.
*
* ⚠️ H3 (plan-set D10, 2026-08-08). Google's own docs disagree: the compute-optimized machine
* family page lists `hyperdisk-balanced` as supported for H3, while the Hyperdisk-Balanced
* machine-series support table marks H3 unsupported. Both agree H3 does NOT support `pd-ssd`.
* H3 is kept here because `hyperdisk-balanced` is weakly dominant over the known-invalid
* `pd-ssd` — worst case an H3 pool fails at scale-up exactly as it would today, best case it
* works. If a real H3 pool ever fails to create, the answer is `pd-balanced`, and that is an enum
* widening (see {@link BOOT_DISK_TYPES}), not a list edit.
*
* ⚠️ Z3 is deliberately ABSENT. Z3 *VMs* do support `pd-ssd`; only Z3 *bare metal* is
* Hyperdisk-only, and GKE node pools are VM shapes. A hypothetical `z3-*-metal` nodeType would
* map to `pd-ssd` and wedge — a recorded caveat, not an encoded case.
*
* Citations — per family, graded by the strongest INDEPENDENT statement found (plan-set §4;
* fetched 2026-08-08). "A" = a verbatim doesn't-support-Persistent-Disk statement on the family's
* own page; "B" = listed as supported in the Hyperdisk-Balanced machine-series support table at
* .../compute/docs/disks/hyperdisks (which DOES discriminate — it marks E2/C2/C2D/G2/A2/M1/N1/
* T2A/T2D/H3 unsupported), i.e. hyperdisk-balanced is confirmed to WORK even where pd-ssd's
* failure is not independently confirmed. B is correct-either-way; A is strictly required.
*   c4d (A) — .../compute/docs/general-purpose-machines: "C4D doesn't support Persistent Disk."
*   n4  (A) — .../kubernetes-engine/docs/how-to/custom-boot-disks: "the N4 machine series only
*             supports `hyperdisk-balanced` as a boot disk."  ← about the GKE node boot disk
*   n4d (A) — .../compute/docs/general-purpose-machines: "N4D doesn't support Persistent Disk or
*             Local SSD. Instead, N4D supports only Hyperdisk volumes."
*   x4  (A) — "You can't attach Persistent Disk volumes to an X4 instance, only Hyperdisk volumes."
*   c4  (A) — "C4: doesn't support Persistent Disk"
*   c4a (A) — "C4A: doesn't support Persistent Disk"
*   m4  (A) — "M4: doesn't support Persistent Disk or Local SSD"
*   h3  (A) — .../compute/docs/compute-optimized-machines enumerates H3 block storage as
*             "Balanced Persistent Disk (pd-balanced), Hyperdisk Balanced (hyperdisk-balanced),
*              Hyperdisk Throughput (hyperdisk-throughput)" — pd-ssd absent. See the H3 caveat.
*   c4n (B) · n4a (B) · m4n (B) · h4d (B) · a4 (B) · a4x (B)
*   a4  also — .../compute/docs/accelerator-optimized-machines: "For durable storage, you can
*             attach Hyperdisk volumes".
*/
const HYPERDISK_ONLY_MACHINE_FAMILIES = [
	"c4",
	"c4a",
	"c4d",
	"c4n",
	"n4",
	"n4a",
	"n4d",
	"x4",
	"m4",
	"m4n",
	"h3",
	"h4d",
	"a4",
	"a4x"
];
/** O(1) membership for {@link bootDiskTypeForMachineType}. Module-level so the Set is built once. */
const HYPERDISK_ONLY_FAMILY_SET = new Set(HYPERDISK_ONLY_MACHINE_FAMILIES);
/**
* The boot-disk type a GKE NodePool for `nodeType` must use (hyperdisk boot-disk D1). The SINGLE
* SOURCE OF TRUTH. provisioning-svc calls this when it writes three fields onto the DataPlane XR:
* `spec.provisionedNodeTypes[].diskType` (`computeProvisionedNodeTypes`),
* `spec.appPoolNodeGroups[].diskType` (`AppPoolNodeGroupReconcileService`) and `spec.nats.diskType`
* (`toXrNatsSizing` in `nats-sizing.ts`). It calls this again when it populates the catalog
* response's `bootDiskType`. R2 (R-081) removed the XRD field `spec.dedicatedNodePools[].diskType`.
* The composition NEVER computes a disk type — it renders what
* the XR carries. The UI NEVER derives one — it reads the server-computed field.
*
* FAMILY = the substring of `nodeType` up to its first `-` (D1). `c4d-standard-32-lssd` → `c4d`.
* A string with no `-` is treated as the whole family (`c4d` → `c4d`).
*
* ⭐ TOTAL BY CONTRACT (security-audit §2). `nodeType` ORIGINATES IN APP FIXTURE MANIFESTS, which
* are app-developer controlled. This function therefore:
*   - accepts ANY string — empty, no dash, absurd length, unicode, control characters — and
*     NEVER throws;
*   - returns ONLY one of the two {@link BOOT_DISK_TYPES} literals, so it can never become an
*     injection vector for the go-template's `diskType` interpolation sink;
*   - defaults UNKNOWN to `'pd-ssd'`, which is the pre-existing platform-wide behaviour and
*     therefore the no-change answer for every family that already works.
* Length and pattern are enforced UPSTREAM (the XRD's `maxLength: 40` + RFC-1123 pattern, and
* {@link MACHINE_TYPE_SLUG_PATTERN}); this function does not re-validate them, it survives them.
*
* The `.toLowerCase()` is deliberate and one-directional: the only failure that matters is
* returning `pd-ssd` for a hyperdisk-only family, and a stray uppercase variant is the cheapest
* way for that to happen. Real GCE machine types are lowercase, so this arm is defence, not
* behaviour.
*
* @param nodeType any machine-type name (opaque; may be malformed)
* @returns `'hyperdisk-balanced'` iff the family is in {@link HYPERDISK_ONLY_MACHINE_FAMILIES},
*          otherwise `'pd-ssd'`
*/
function bootDiskTypeForMachineType(nodeType) {
	const dash = nodeType.indexOf("-");
	const family = (dash === -1 ? nodeType : nodeType.slice(0, dash)).toLowerCase();
	return HYPERDISK_ONLY_FAMILY_SET.has(family) ? "hyperdisk-balanced" : "pd-ssd";
}
/**
* GCE machine-type FAMILIES that are confirmed to boot from `pd-ssd` (compute-classes D6).
*
* ⭐ WHY THIS LIST EXISTS AS A LIST, rather than being derived as
* `SUPPORTED_MACHINE_TYPE_FAMILIES \ HYPERDISK_ONLY_MACHINE_FAMILIES`: that set is documented as
* a GRAMMAR check where *"adding a family HERE is always safe (grammar only)"*. Deriving a
* disk-type decision from it would silently make that sentence FALSE — the next author who adds a
* family to the grammar list so a publish stops being blocked would also, invisibly, assert that
* the family boots from `pd-ssd`. That is the c4d wedge with a new entry point.
*
* ⛔ The two lists are pinned as a PARTITION of {@link SUPPORTED_MACHINE_TYPE_FAMILIES} by a test:
* every grammar family appears in exactly ONE of this list and
* {@link HYPERDISK_ONLY_MACHINE_FAMILIES}, and the two are disjoint. A family added to the
* grammar list and to neither of these reds that test, which is the whole point — the alternative
* is a new family that silently takes {@link platformChosenBootDiskType}'s
* `hyperdisk-balanced` default, which is CORRECT-BY-DEFAULT here but must still be a deliberate
* entry rather than an omission.
*
* MEMBERSHIP is exactly the eighteen families the `⛔ NEVER move a pd-ssd-CAPABLE family into
* this list` warning on {@link HYPERDISK_ONLY_MACHINE_FAMILIES} already enumerates, transcribed —
* NOT re-researched. Two of them carry the caveats recorded there:
*   - `z3` — Z3 *VMs* support `pd-ssd`; only Z3 *bare metal* is Hyperdisk-only, and GKE node pools
*     are VM shapes. A hypothetical `z3-*-metal` nodeType maps here and would wedge; a recorded
*     caveat, not an encoded case.
*   - `g4` / `m2` — real PD-capable families that were MISSING from the grammar list until
*     2026-08-08 and were found by eye. They are here for the same reason: leaving a
*     known-PD-capable family out would flip it to Hyperdisk and trigger a rolling node
*     recreation across every existing pool of that family, fleet-wide.
*/
const PD_SSD_CAPABLE_MACHINE_FAMILIES = [
	"e2",
	"n1",
	"n2",
	"n2d",
	"c2",
	"c2d",
	"c3",
	"c3d",
	"g2",
	"g4",
	"a2",
	"a3",
	"m1",
	"m2",
	"m3",
	"t2d",
	"t2a",
	"z3"
];
/** O(1) membership for {@link platformChosenBootDiskType}. Module-level so the Set is built once. */
const PD_SSD_CAPABLE_FAMILY_SET = new Set(PD_SSD_CAPABLE_MACHINE_FAMILIES);
/**
* The boot-disk type a GKE NodePool must use for a machine type **the PLATFORM chose**
* (compute-classes D6). The INVERTED-default twin of {@link bootDiskTypeForMachineType}.
*
* ## The inversion, and why it is safe here and not there
*
* {@link bootDiskTypeForMachineType} defaults an UNKNOWN family to `pd-ssd` because that is the
* pre-existing platform-wide behaviour and therefore the no-change answer for every family that
* already works. Its own docblock records that this *"goes stale in the dangerous direction by
* default"* — every GCE family shipped since C4 has been Hyperdisk-only.
*
* When the platform's own newest-first class resolver picked the family, that staleness becomes a
* near-certainty rather than a risk: the resolver reaches a brand-new (invariably Hyperdisk-only)
* family the moment discovery sees it, which is BEFORE any human updates
* {@link HYPERDISK_ONLY_MACHINE_FAMILIES}. The `pd-ssd` default is then the wedge — a demand pool
* created at `initialNodeCount: 0` goes RUNNING and Crossplane goes Ready, and the invalid
* machineType×diskType pairing only surfaces at first scale-up.
*
* ⇒ **Unknown here means `hyperdisk-balanced`**, and `pd-ssd` is returned ONLY for a family
* positively listed in {@link PD_SSD_CAPABLE_MACHINE_FAMILIES}. The failure directions are not
* symmetric and this picks the survivable one: a wrong `hyperdisk-balanced` on a PD-only family
* fails pool creation exactly as loudly as today, while a wrong `pd-ssd` on a Hyperdisk-only
* family wedges silently for hours.
*
* ## ⛔ Why a SIBLING FUNCTION and not a `chosenBy` argument on the existing one
*
* A defaulted `chosenBy: 'app' | 'platform' = 'app'` parameter would keep all three existing call
* sites compiling — and that is the problem. It makes the DANGEROUS default the implicit one at
* every site and lets a new site inherit it by OMISSION, which is invisible in a diff. A
* separately-named function makes the choice appear at each call site as a symbol a reviewer can
* see. It also leaves the existing function's contract byte-intact, which D6 does not authorise
* changing.
*
* ## Totality
*
* Identical posture to its twin: accepts ANY string (empty, no dash, absurd length, unicode,
* control characters), never throws, and returns only one of the two {@link BOOT_DISK_TYPES}
* literals — so it can never become an injection vector for the go-template's `diskType` sink.
* The `.toLowerCase()` inside {@link bootDiskTypeForMachineType}'s family extraction is
* reproduced here for the same one-directional reason.
*
* @param nodeType any machine-type name the PLATFORM selected (opaque; may be malformed)
* @returns `'pd-ssd'` iff the family is in {@link PD_SSD_CAPABLE_MACHINE_FAMILIES}, otherwise
*          `'hyperdisk-balanced'`
*/
function platformChosenBootDiskType(nodeType) {
	const dash = nodeType.indexOf("-");
	const family = (dash === -1 ? nodeType : nodeType.slice(0, dash)).toLowerCase();
	return PD_SSD_CAPABLE_FAMILY_SET.has(family) ? "pd-ssd" : "hyperdisk-balanced";
}
/** One provisionable+enabled machine type in this DP (GPU folded in; D6). `.strict()` — unknown keys rejected
*  (mass-assignment defense on the XR-write + response paths). */
const machineTypeCatalogEntrySchema = z.object({
	/** OPAQUE platform name the app maps onto and POSTs back as `node.nodeType`. */
	nodeType: z.string().min(1),
	displayName: z.string().min(1),
	cpuMilliCeiling: z.number().int().positive(),
	memoryMiBCeiling: z.number().int().positive(),
	/** Present iff GPU-backed (the retired special-case `gpu` block, folded in). */
	gpu: z.object({
		acceleratorType: z.string().min(1),
		count: z.number().int().positive(),
		/**
		* Accelerator-capable zones (node-pool demand-provisioning D9): the zones in which THIS machine
		* type's accelerator actually exists. Populated by WS-2 discovery from live
		* `MachineType.accelerators[]` + `AcceleratorTypesClient` (per-zone, D3a/D14). OPTIONAL here for
		* additive back-compat (SYS-020) — a legacy persisted catalog/enabled entry may lack it and must
		* still validate (this same schema is re-`.parse()`d by the sdk-node catalog client, provisioning-svc
		* + site-admin DTOs, and core-api's `.strict()` mirror). Discovery always populates it going forward;
		* WS-2 PROJECTS it (required) into {@link provisionedNodeTypeSchema} when computing the XR field.
		* Non-empty when present.
		*/
		zones: z.array(z.string().min(1)).min(1).optional()
	}).strict().optional(),
	/**
	* Approximate LIVE on-demand price in USD per hour for this machine type in the DP's region, summed
	* from the region's Compute Engine SKUs (vCPU-core + RAM, plus the accelerator SKU for GPU types).
	* OPTIONAL + additive (SYS-020 back-compat): UNDEFINED when the price is unknown or the type cannot be
	* confidently mapped to its SKUs — the UI renders "—" and NEVER a guessed number. Advisory display only
	* (it is NOT persisted into the DP's desired-state selection): the menu endpoint enriches served entries
	* at serve time; the same `.strict()` schema is re-`.parse()`d by the sdk-node catalog client,
	* provisioning-svc + site-admin DTOs, and core-api's mirror, so a legacy entry lacking it still validates.
	*/
	pricePerHourUsd: z.number().nonnegative().optional(),
	/**
	* Approximate LIVE spot price in USD per hour for this machine type in the DP's region
	* (2026-10-24-pool-machine-generations, D-38, R-045). The sibling of {@link pricePerHourUsd}
	* for spot capacity: the menu endpoint of provisioning-svc (WS-3f) sets it at serve time when
	* the region's Spot SKUs price the type, and leaves the key absent when they do not. The UI
	* then shows no spot price, never a guessed one. OPTIONAL and additive (SYS-020): a legacy
	* entry lacking it still validates. Advisory display only, never persisted into the DP's
	* desired-state selection, by the same seam as {@link pricePerHourUsd}. A negative number, a
	* non-number, `Infinity` and `NaN` fail the parse at this key: zod 4's `z.number()` refuses a
	* non-finite value, so a parsed entry never holds a price that `JSON.stringify` writes as `null`.
	*/
	spotPricePerHourUsd: z.number().nonnegative().optional(),
	/**
	* Quota-HEADROOM count: how many of this machine type the DP's project is ALLOWED to add in the region
	* right now, from live quota (limit − usage) ÷ the per-unit cost. This is a quota ceiling, NOT physical
	* stock. OPTIONAL + additive (SYS-020): UNDEFINED when the relevant quota is unknown/absent. Advisory
	* display only (not persisted into desired-state). See {@link availableBasis} for how the UI must label
	* the two quota kinds.
	*/
	availableCount: z.number().int().nonnegative().optional(),
	/**
	* How {@link availableCount} was derived, so the UI can label it correctly: `'gpu-quota'` = a dedicated
	* per-accelerator GPU quota (count ÷ GPUs-per-node); `'cpu-quota-shared'` = the region's SHARED `CPUS`
	* quota (count ÷ vCPUs-per-node) — headroom the type shares with every other CPU machine type, so it is
	* an upper bound, not a reservation. OPTIONAL + additive. Serve-time enrichment always POPULATES this
	* together with `availableCount` (both come from the same quota computation), but the schema keeps the
	* two INDEPENDENTLY optional — no `.refine()` cross-field rule — so this `.strict()` shape (and core-api's
	* mirror of it) stays additive/forward-compatible for a legacy or partial entry.
	*/
	availableBasis: z.enum(["gpu-quota", "cpu-quota-shared"]).optional(),
	/**
	* Serve-time/enabled marker — exactly-one `true` among a DP's enabled machine types; the DP default the
	* opaque `'standard'` tier resolves to (see {@link DEFAULT_TIER_KEY}). OPTIONAL + additive (SYS-020
	* back-compat), mirroring the sibling optional fields above: a legacy/partial entry (the discovered
	* region catalog, serve-time enrichment, a persisted `dp_enabled_machine_types` row, the sdk-node
	* catalog client, core-api's `.strict()` mirror) may lack it and must still validate. The producer
	* (provisioning-svc canonicalization, WS-2/D2) INJECTS/keeps exactly-one `isDefault: true`; the consumer
	* (operator compute-unit broker, WS-3/D3) reads it to resolve `'standard'`. Distinct from the
	* storage-class entry's REQUIRED `isDefault` — the MT entry's is optional for the legacy-path back-compat
	* above.
	*/
	isDefault: z.boolean().optional(),
	/**
	* SERVER-COMPUTED boot-disk type for this machine type (hyperdisk boot-disk D5) — the GKE
	* NodePool `nodeConfig.diskType` a pool for this type will be created with. Derived at READ
	* TIME from `nodeType` via {@link bootDiskTypeForMachineType}; **NEVER PERSISTED** and never
	* accepted as authoritative from a client.
	*
	* OPTIONAL for two independent reasons, both real:
	*  1. LEGACY ROWS — a persisted `dp_enabled_machine_types` row / materialized
	*     `machine_type_catalog` row / older writer carries no `bootDiskType`, and this same
	*     `.strict()` schema re-parses all of them (SYS-020 additive back-compat), exactly like
	*     the sibling optional fields above.
	*  2. NON-GCP — `local` (k3d) pools have no disk-type concept, so the field is simply absent
	*     on a non-gcp catalog entry rather than being given a meaningless `pd-ssd`.
	*
	* WHY IT LIVES ON THIS SCHEMA and not on a CP-only carrier, given the #771 note above: the
	* site-admin UI ECHOES catalog entries back on the per-DP machine-types PATCH, whose body is
	* `z.array(machineTypeCatalogEntrySchema).min(1)` under the global strict validation pipe — a
	* separate carrier would 400 that round-trip. The reason this is SAFE where a top-level
	* `zones` was not: `canonicalizeEnabled`
	* (provisioning-svc `src/lib/machine-type-catalog/machine-type-pool.ts:54`) replaces every
	* submitted entry with the authoritative UNENRICHED catalog entry before anything is
	* persisted, so no `bootDiskType`-carrying entry can ever reach the desired-state envelope,
	* the operator ConfigMap, core-api, or a published `@zion/sdk-node` client. Same seam that
	* already keeps `pricePerHourUsd` / `availableCount` off the persisted set.
	*
	* ⛔ D13 — ENRICHMENT HAPPENS ONLY AT RESPONSE ASSEMBLY. This field must never be attached to
	* a catalog value that flows into `canonicalizeEnabled`, into persistence, or onto the
	* release-engine→DP desired-state path.
	*/
	bootDiskType: z.enum(BOOT_DISK_TYPES).optional()
}).strict();
/**
* The grammar of an operator-authored machine-type SLUG — the value a fixture puts in
* `services[].machineType` / `workers[].machineType` (R-9 M-1) and the immutable key of a
* site-wide catalog entry (M-10).
*
* An RFC-1123 label, capped at 40 characters. The cap is not cosmetic: the slug is destined for a
* GKE nodePoolId, which GCP caps at 40, and the XRD already pins `provisionedNodeTypes[].nodeType`
* to `maxLength: 40` for exactly that reason.
*
* ⛔ THE SLUG IS OPAQUE BY CONTRACT. The seeded values will LOOK like GCP SKUs (`n2-standard-4`)
* because they are derived from them (M-11), and that resemblance is the whole hazard: nothing may
* parse a slug, infer a family from it, or assume it names anything in any cloud. The
* `<family>-<class>-<size>` grammar and the {@link SUPPORTED_MACHINE_TYPE_FAMILIES} check apply to
* the LEGACY `nodeType` field and to nothing else.
*/
const MACHINE_TYPE_SLUG_PATTERN = /^[a-z0-9]([a-z0-9-]{0,38}[a-z0-9])?$/;
/**
* True iff `x` is a well-formed machine-type slug ({@link MACHINE_TYPE_SLUG_PATTERN}). Shape only —
* MEMBERSHIP in a DP's enabled catalog is authoritative at reconcile, fail-closed.
*
* ⛔ THE `typeof` GUARD IS LOAD-BEARING, not defensive noise.
* `RegExp.test` STRINGIFIES its argument, so `MACHINE_TYPE_SLUG_PATTERN.test(undefined)` tests the
* nine-letter string `"undefined"` — which this grammar ACCEPTS. Measured: `undefined`, `null`,
* `123`, `true` and `['gpu-large']` all passed the bare pattern, so this predicate returned `true`
* for every one of them. A single-element array is the worst arm: it stringifies to its element and
* therefore impersonates a VALID slug rather than an obviously broken one.
*
* ⛔ THE PARAMETER IS `unknown`, DELIBERATELY. Typing it `string` made the `typeof` branch
* unreachable *to the compiler* while remaining reachable *at runtime* — this package is consumed
* from plain JS and across package boundaries. Widening a parameter is not a breaking change (every
* `string` is an `unknown`), and it lets the spec pin the coercion without the `as unknown as
* string` double-cast this repo bans.
*
* ⭐ THIRD KNOWN INSTANCE of this class: `parseAppSlug` (provisioning-svc — consequence was an
* `app_undefined` schema/role name) and `assertClaimId` (`@zion/sdk-node`, PR #1655 — consequence
* was a literal `/v1/fixtures/undefined/...` request). All three were survivable ONLY because a
* second, unrelated gate happened to catch them — here, catalog membership at reconcile. That is
* why the class is invisible to incident counts and must be found by sweeping for `.test()` on
* caller-supplied values.
*/
function isMachineTypeSlug(x) {
	return typeof x === "string" && MACHINE_TYPE_SLUG_PATTERN.test(x);
}
/**
* @deprecated R-9 M-2 — the opaque default-tier sentinel is retired. The catalog's default is a real
* slug (`MachineTypeSlugCatalogEntry.isDefault`), so nothing new should send `'standard'`. RETAINED and
* still honoured by the compute-unit broker because removing it is a RUNTIME break for any already-
* deployed app that sends it — an app-side change we cannot make and have not measured. Removed once
* the broker reports zero sentinel requests; same discipline as `nodeType` (M-11) and `sizeClass` (G-2).
*
* The single opaque "default tier" sentinel an APP sends for `node.nodeType` and `volume.storageClass`
* when it means "whatever this DP's default is", rather than a concrete machine-type / storage-class name
* (LF-7). App-sends-opaque, platform-resolves-to-default: the operator's compute-unit broker maps
* `node.nodeType === 'standard'` → the DP's `isDefault` machine type, and `volume.storageClass === 'standard'`
* → the DP's `isDefault` storage class (D3). ONLY this exact literal maps to the default — any other unknown
* key still fail-closes against the concrete catalog (no silent fallback; typo safety). The app template's
* `WORKSPACE_COMPUTE_SPEC`/`NURSERY_COMPUTE_SPEC` already send this key; WS-3 makes the platform honor it.
*/
const DEFAULT_TIER_KEY = "standard";
/**
* @deprecated R-9 M-2 — see {@link DEFAULT_TIER_KEY}. Deprecated, still honoured, deleted on a
* measured zero.
*
* The opaque default-tier sentinels — BOTH resolve to the DP's `isDefault` machine type / storage class.
* `'standard'` is the original (legacy + app templates' `WORKSPACE_COMPUTE_SPEC`/`NURSERY_COMPUTE_SPEC`
* send it); `'default'` is the clearer alias the app can send instead. `DEFAULT_TIER_KEY` stays `'standard'`
* so existing senders are unaffected; consumers route the decision through {@link isDefaultTierKey}, so both
* literals resolve identically with no consumer edits.
*/
const DEFAULT_TIER_KEY_ALIASES = ["standard", "default"];
/**
* @deprecated R-9 M-2 — see {@link DEFAULT_TIER_KEY}. Deprecated, still honoured, deleted on a
* measured zero.
*
* True iff `x` is a default-tier sentinel ({@link DEFAULT_TIER_KEY_ALIASES}: `'standard'` or `'default'`).
* The operator broker (WS-3) uses this to decide whether a requested `nodeType`/`storageClass` resolves to
* the DP default; every non-sentinel key is resolved against the concrete catalog and fail-closes if absent.
* Note this is an EXACT match on the whole string — a concrete name that merely CONTAINS `standard`/`default`
* (e.g. `e2-standard-4`, `standard-rwo`, `defaulty`) is NOT a sentinel and returns `false`.
*/
function isDefaultTierKey(x) {
	return DEFAULT_TIER_KEY_ALIASES.includes(x);
}
/**
* One entry of the `DataPlane` XR field `spec.provisionedNodeTypes` — the demand-driven machine-type
* NodePool set (node-pool demand-provisioning D2/D9/D13). A DEDICATED MINIMAL subset of
* {@link MachineTypeCatalogEntry} (drops `displayName` + the CPU/mem ceilings, which the composition never
* reads) — NOT the full entry (D13 revised): the WS-3 XRD mirrors this 1:1 (INV-001, no dead fields on the
* XR) and the go-template renders exactly its fields. provisioning-svc's `computeProvisionedNodeTypes`
* (WS-2) PROJECTS the discovered catalog entries down to this shape for the intersected node types
* (`enabled ∩ in-use`); WS-3's go-template ranges over the XR array to emit one GKE NodePool per entry,
* pinning GPU `nodeLocations` to `gpu.zones` (D9). `.strict()` — unknown keys rejected (mass-assignment
* defense on the XR-write path).
*/
const provisionedNodeTypeSchema = z.object({
	/** The machine type provisioned as a NodePool (== the catalog entry's `nodeType`). */
	nodeType: z.string().min(1),
	/**
	* Present iff GPU-backed. `zones` is REQUIRED here (unlike the catalog entry's optional `zones`): a
	* provisioned GPU pool with no accelerator-capable zone is EXACTLY the wedge D9 eliminates, so WS-2
	* MUST populate zones before writing a GPU entry — a GPU entry without a non-empty `zones` is rejected
	* (fail-loud). The go-template renders `guestAccelerators` + pins `nodeLocations` to these zones.
	*/
	gpu: z.object({
		acceleratorType: z.string().min(1),
		count: z.number().int().positive(),
		zones: z.array(z.string().min(1)).min(1)
	}).strict().optional(),
	/**
	* Pool-placement zones for this node type — rendered as the NodePool's `nodeLocations` for EVERY entry,
	* GPU or not (issue #771, D-Z1/D-Z5). Projected from the catalog entry's top-level `zones`.
	*
	* OPTIONAL, unlike `gpu.zones` above, for one specific reason: an XR written by the PRE-#771
	* provisioning-svc carries `gpu.zones` and no top-level `zones`, and the chart upgrade is not atomic
	* with the re-patch that adds it. The go-template therefore falls back to `gpu.zones` when this is
	* absent (D-Z5) — without that fallback a GPU pool would momentarily lose its pin mid-upgrade and could
	* attempt an uncreatable pool, reintroducing the very wedge this closes. A projected entry is otherwise
	* always populated, because an entry with an empty zone set never reaches the catalog (D-Z4).
	*
	* Non-empty when present. The XRD constrains each element with the zone pattern
	* `^[a-z]+-[a-z]+[0-9]+-[a-z]$`, which is the SOLE guard on the go-template's UNQUOTED interpolation of
	* these values (D-Z6) — do not relax it.
	*/
	zones: z.array(z.string().min(1)).min(1).optional(),
	/**
	* The GKE NodePool boot-disk type for this pool (hyperdisk boot-disk D2), rendered by the
	* composition as `nodeConfig.diskType`. Projected by provisioning-svc's
	* `computeProvisionedNodeTypes` from `nodeType` via {@link bootDiskTypeForMachineType} —
	* ALWAYS, for every entry, hyperdisk family or not. Never client-supplied.
	*
	* OPTIONAL, for the SAME reason `zones` above is optional and NOT a hint that it may be
	* skipped: an XR written by the PRE-change provisioning-svc carries no `diskType`, and the
	* chart upgrade is not atomic with the re-patch that adds it. The go-template therefore
	* defaults to `pd-ssd` when this is absent (D7 — exact analogue of the #771 D-Z5 `gpu.zones`
	* fallback). A freshly-projected entry is ALWAYS populated.
	*
	* The XRD constrains this to `enum: [pd-ssd, hyperdisk-balanced]`, and that enum is the SOLE
	* YAML-injection guard on the go-template's UNQUOTED interpolation of this value (D2, same
	* rationale as D-Z6 for `zones`) — do not relax it, and do not add a value to
	* {@link BOOT_DISK_TYPES} without widening the XRD in the same change.
	*
	* ⛔ It is ALSO part of the reconciler's idempotence key (`sameProvisionedNodeTypes`, D3).
	* That is not an optimisation — it is the migration mechanism: the live XR entries written
	* before this change carry no `diskType`, so including it in the key is what makes them
	* compare DRIFTED and get re-patched. Leaving it out ships a green PR that changes nothing.
	*/
	diskType: z.enum(BOOT_DISK_TYPES).optional()
}).strict();
/**
* Stable GCP machine-type FAMILY prefixes (node-pool demand-provisioning D10/D19). This is the ONLY static
* machine-type list in the platform — FAMILIES, not SKUs (honors D3's dynamic discovered catalog). WS-6's
* `zion-app validate-app` D10 publish check validates a fixture `nodeType`'s name SHAPE
* (`<family>-<class>-<size>`) against this set — catching gross typos OFFLINE with zero SKU-list
* maintenance. Authoritative MEMBERSHIP is the operator's reconcile-time `MachineTypeCatalog.resolve`
* against the DP's live enabled∩available catalog (fail-closed, D10). Families change rarely; extend as GCP
* ships new families. NOT exhaustive of every GCP family — the reconcile check is the backstop.
*
* ⛔ THIS IS A GRAMMAR CHECK, NOT AN ALLOWLIST, AND IT GOES STALE SILENTLY IN THE BLOCKING DIRECTION.
* It answers only "does this LOOK like a machine-type name" offline at publish; it never decides what a
* DP may actually run. But a family missing from it makes `zion-app validate-app` REFUSE TO PUBLISH a
* fixture naming that family — and nothing anywhere reports the list as out of date.
*
* WHY THE LIST GREW ON 2026-08-08 (hyperdisk boot-disk, D28): it predated every 4th-generation family, so
* it rejected `c4d-standard-32-lssd` — THE EXACT FAMILY the platform had just fixed node-pool provisioning
* for. All fourteen hyperdisk-only families (D11) were absent, i.e. the platform could provision Hyperdisk
* pools that no app was permitted to ask for. **17 families added, 32 total.** The D11 fourteen (`c4, c4a,
* c4d, c4n, n4, n4a, n4d, x4, m4, m4n, h3, h4d, a4, a4x`) plus three real PD-capable families that were
* simply missing: `z3`, `g4`, `m2`. The last three are not hyperdisk families and so are NOT covered by
* the cross-pin below — they were found by eye, which is exactly why the cross-pin cannot be the only
* defence and why the maintenance step is to re-read the region catalog, not to trust this list.
*
* ⛔ The two lists are cross-pinned by a test: every member of {@link HYPERDISK_ONLY_MACHINE_FAMILIES}
* must appear here. Add a family to the boot-disk map and forget this list, and that test goes red —
* which is the whole point, because the failure it replaces was invisible. Adding a family HERE is
* always safe (grammar only); omitting one is what blocks a publish.
*/
const SUPPORTED_MACHINE_TYPE_FAMILIES = [
	"e2",
	"n1",
	"n2",
	"n2d",
	"c2",
	"c2d",
	"c3",
	"c3d",
	"t2d",
	"t2a",
	"g2",
	"g4",
	"a2",
	"a3",
	"m1",
	"m2",
	"m3",
	"c4",
	"c4a",
	"c4d",
	"c4n",
	"n4",
	"n4a",
	"n4d",
	"x4",
	"m4",
	"m4n",
	"h3",
	"h4d",
	"a4",
	"a4x",
	"z3"
];
/** One available PVC storage class in this DP. The app picks `volume.storageClass` from this list (or omits
*  it → operator uses the `isDefault` one). */
const storageClassCatalogEntrySchema = z.object({
	name: z.string().min(1),
	isDefault: z.boolean()
}).strict();
/** core-api route for `listEnabledMachineTypes` (org-scoped, JWT). Serves a BARE array (D24). Shared:
*  WS-3's CG-C catalog controller + WS-5's admin proxy IMPORT this so the route can't drift. */
const MACHINE_TYPE_CATALOG_PATH = "/v1/compute/machine-types";
/** core-api route for `listStorageClasses` (org-scoped, JWT). Serves a BARE array (D24). Shared with WS-3/WS-5 (as above). */
const STORAGE_CLASS_CATALOG_PATH = "/v1/compute/storage-classes";
//#endregion
export { BOOT_DISK_TYPES, DEFAULT_TIER_KEY, DEFAULT_TIER_KEY_ALIASES, HYPERDISK_ONLY_MACHINE_FAMILIES, MACHINE_TYPE_CATALOG_PATH, MACHINE_TYPE_SLUG_PATTERN, PD_SSD_CAPABLE_MACHINE_FAMILIES, STORAGE_CLASS_CATALOG_PATH, SUPPORTED_MACHINE_TYPE_FAMILIES, bootDiskTypeForMachineType, isDefaultTierKey, isMachineTypeSlug, machineTypeCatalogEntrySchema, platformChosenBootDiskType, provisionedNodeTypeSchema, storageClassCatalogEntrySchema };

//# sourceMappingURL=machine-type-catalog.js.map